import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Phone, Mail, Clock, Waves } from "lucide-react";
import { SiFacebook, SiInstagram, SiWhatsapp } from "react-icons/si";
import { motion, AnimatePresence } from "framer-motion";
import { ROUTE_PATHS } from "@/lib/index";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
interface LayoutProps {
  children: React.ReactNode;
}
const NAV_ITEMS = [{
  name: "Inicio",
  path: ROUTE_PATHS.HOME
}, {
  name: "Espacios",
  path: ROUTE_PATHS.ESPACIOS
}, {
  name: "Menú",
  path: ROUTE_PATHS.MENU
}, {
  name: "Contacto",
  path: ROUTE_PATHS.CONTACTO
}];
export function Layout({
  children
}: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cierra el menú móvil al cambiar de ruta
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);
  return <div className="flex min-h-screen flex-col bg-background selection:bg-primary/20 selection:text-primary">
      {/* Navigation Header */}
      <header className={cn("fixed top-0 z-50 w-full h-20 transition-all duration-300 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-transparent border-transparent")}>
        <div className="container mx-auto h-full px-4 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 group transition-transform active:scale-95">
            <div className="bg-primary text-primary-foreground p-1.5 rounded-lg">
              <Waves className="w-6 h-6" />
            </div>
            <div className="flex flex-col">
              <span className="font-display text-xl font-bold tracking-tight leading-none text-foreground">
                LAS GAVIOTAS
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Playa El Maviri
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map(item => <NavLink key={item.path} to={item.path} className={({
            isActive
          }) => cn("relative text-sm font-medium transition-colors hover:text-primary", isActive ? "text-primary" : "text-muted-foreground")}>
                {({
              isActive
            }) => <>
                    {item.name}
                    {isActive && <motion.div layoutId="nav-underline" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" transition={{
                type: "spring",
                stiffness: 380,
                damping: 30
              }} />}
                  </>}
              </NavLink>)}
            <Button asChild variant="default" className="rounded-full px-6 shadow-md hover:shadow-lg transition-all">
              <Link to={ROUTE_PATHS.CONTACTO}>Reservar</Link>
            </Button>
          </nav>

          {/* Mobile Menu Trigger */}
          <div className="md:hidden flex items-center gap-4">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l border-border bg-background">
                <SheetHeader className="text-left border-b pb-4 mb-6">
                  <SheetTitle className="font-display text-2xl font-bold text-primary">
                    LAS GAVIOTAS
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4">
                  {NAV_ITEMS.map(item => <NavLink key={item.path} to={item.path} className={({
                  isActive
                }) => cn("flex items-center text-lg font-medium p-3 rounded-xl transition-all", isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
                      {item.name}
                    </NavLink>)}
                  <hr className="my-4 border-border" />
                  <div className="flex flex-col gap-4 px-3">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>+52 (668) 123 4567</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>Playa El Maviri, Sinaloa</span>
                    </div>
                  </div>
                  <Button asChild className="mt-6 rounded-xl w-full">
                    <Link to={ROUTE_PATHS.CONTACTO}>Contáctanos</Link>
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Waves className="w-8 h-8 text-primary" />
                <span className="font-display text-2xl font-bold tracking-tight text-foreground">
                  LAS GAVIOTAS
                </span>
              </div>
              <p className="text-muted-foreground leading-relaxed max-w-xs">
                Tu refugio paradisíaco en el Mar de Cortés. Gastronomía de autor, descanso frente al mar y la mejor vista de Playa El Maviri.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="p-2 rounded-full bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-all shadow-sm">
                  <SiFacebook className="w-5 h-5" />
                </a>
                <a href="#" className="p-2 rounded-full bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-all shadow-sm">
                  <SiInstagram className="w-5 h-5" />
                </a>
                <a href="#" className="p-2 rounded-full bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-all shadow-sm">
                  <SiWhatsapp className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-6">
              <h4 className="font-sans font-semibold text-lg text-foreground">Navegación</h4>
              <ul className="space-y-3">
                {NAV_ITEMS.map(item => <li key={item.path}>
                    <Link to={item.path} className="text-muted-foreground hover:text-primary transition-colors inline-block">
                      {item.name}
                    </Link>
                  </li>)}
                <li>
                  <Link to={ROUTE_PATHS.ESPACIOS} className="text-muted-foreground hover:text-primary transition-colors">
                    Cabañas
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h4 className="font-sans font-semibold text-lg text-foreground">Ubicación</h4>
              <ul className="space-y-4">
                <li className="flex gap-3 text-muted-foreground">
                  <MapPin className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm leading-snug">
                    Isla El Maviri s/n, Bahía de Topolobampo, <br />
                    Ahome, Sinaloa, CP 81200
                  </span>
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm">+52 (668) 168 90 60</span>
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm">info@turismolasgaviotas.mx</span>
                </li>
              </ul>
            </div>

            {/* Working Hours */}
            <div className="space-y-6">
              <h4 className="font-sans font-semibold text-lg text-foreground">Horarios</h4>
              <ul className="space-y-4">
                <li className="flex gap-3 text-muted-foreground">
                  <Clock className="w-5 h-5 text-primary shrink-0" />
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-foreground">Restaurantes</p>
                    <p>Lun - Dom: 10:00 AM - 8:00 PM</p>
                  </div>
                </li>
                <li className="flex gap-3 text-muted-foreground">
                  <Clock className="w-5 h-5 text-primary shrink-0" />
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-foreground">Cabañas</p>
                    <p>Recepción 24 Horas</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground font-mono">
              © 2026 Complejo Turístico Las Gaviotas. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              <Link to="#" className="hover:text-primary transition-colors">Privacidad</Link>
              <Link to="#" className="hover:text-primary transition-colors">Términos</Link>
              <Link to="#" className="hover:text-primary transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Float Action Button (Mobile Only) */}
      <a href="https://wa.me/526681234567" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 md:hidden bg-green-500 text-white p-4 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all">
        <SiWhatsapp className="w-6 h-6" />
      </a>
    </div>;
}